import React, { useEffect, useRef } from 'react';
import {
    ColorType,
    CrosshairMode,
    LineSeries,
    createChart,
    createSeriesMarkers,
    type IChartApi,
    type SeriesMarker,
    type Time
} from 'lightweight-charts';
import { convertEstTimestampToTradingView } from '@/utils/dateHelpers.ts';

type ObvLineAuditPoint = {
    dateTime: string;
    signal: number | null;
    timestamp: number;
    value: number;
};

type ObvLineAuditEventMarker = {
    dateTime: string;
    position: 'aboveBar' | 'belowBar' | 'inBar';
    text: string;
    tone: 'bullish' | 'bearish' | 'neutral';
};

interface OBVLineAuditChartProps {
    eventMarkers: ObvLineAuditEventMarker[];
    focusedDateTime: string | null;
    height?: number;
    points: ObvLineAuditPoint[];
}

const TONE_COLOR: Record<ObvLineAuditEventMarker['tone'], string> = {
    bullish: '#26a69a',
    bearish: '#ef5350',
    neutral: '#facc15'
};

const buildSeriesMarkers = (params: {
    eventMarkers: ObvLineAuditEventMarker[];
    focusedDateTime: string | null;
    points: ObvLineAuditPoint[];
}): SeriesMarker<Time>[] => {
    const groupedMarkers = new Map<string, SeriesMarker<Time>>();
    const pointTimestampByDateTime = new Map<string, number>();

    for (const point of params.points) {
        pointTimestampByDateTime.set(point.dateTime, point.timestamp);
    }

    for (const marker of params.eventMarkers) {
        const eventTimestamp = pointTimestampByDateTime.get(marker.dateTime);

        if (eventTimestamp === undefined) {
            continue;
        }

        const time = convertEstTimestampToTradingView(eventTimestamp);

        const key = `${String(time)}:${marker.position}`;
        const existingMarker = groupedMarkers.get(key);

        if (existingMarker) {
            const combinedText = [existingMarker.text, marker.text]
                .filter((text, index, values) => values.indexOf(text) === index)
                .join(' | ');

            groupedMarkers.set(key, {
                ...existingMarker,
                text: combinedText
            });

            continue;
        }

        groupedMarkers.set(key, {
            time,
            position: marker.position,
            shape: marker.position === 'aboveBar' ? 'arrowDown' : marker.position === 'belowBar' ? 'arrowUp' : 'circle',
            color: TONE_COLOR[marker.tone],
            text: marker.text
        });
    }

    if (!params.focusedDateTime) {
        return [...groupedMarkers.values()];
    }

    const focusedPoint = params.points.find((point) => point.dateTime === params.focusedDateTime);

    if (!focusedPoint) {
        return [...groupedMarkers.values()];
    }

    const focusKey = `${String(convertEstTimestampToTradingView(focusedPoint.timestamp))}:inBar`;

    if (!groupedMarkers.has(focusKey)) {
        groupedMarkers.set(focusKey, {
            time: convertEstTimestampToTradingView(focusedPoint.timestamp),
            position: 'inBar',
            shape: 'circle',
            color: '#60a5fa',
            text: 'Focus'
        });
    }

    return [...groupedMarkers.values()];
};

const OBVLineAuditChart: React.FC<OBVLineAuditChartProps> = ({ eventMarkers, focusedDateTime, height = 280, points }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);

    useEffect(() => {
        if (!chartContainerRef.current || points.length === 0) {
            return;
        }

        chartRef.current?.remove();
        chartRef.current = null;

        const chart = createChart(chartContainerRef.current, {
            width: chartContainerRef.current.clientWidth,
            height,
            layout: {
                textColor: '#d4d4d8',
                background: {
                    type: ColorType.Solid,
                    color: '#111111'
                }
            },
            grid: {
                vertLines: { color: '#27272a' },
                horzLines: { color: '#27272a' }
            },
            crosshair: {
                mode: CrosshairMode.Normal
            },
            timeScale: {
                borderColor: '#3f3f46',
                timeVisible: true,
                secondsVisible: false
            },
            rightPriceScale: {
                borderColor: '#3f3f46'
            }
        });

        chartRef.current = chart;

        const obvSeries = chart.addSeries(LineSeries, {
            color: '#22c55e',
            lineWidth: 2,
            priceLineVisible: true,
            title: 'OBV'
        });

        const signalSeries = chart.addSeries(LineSeries, {
            color: '#60a5fa',
            lineWidth: 2,
            lineStyle: 2,
            priceLineVisible: true,
            title: 'Signal'
        });

        const obvData: { time: Time; value: number }[] = [];
        const signalData: { time: Time; value: number }[] = [];
        const seenObvTimes = new Set<number>();
        const seenSignalTimes = new Set<number>();

        for (const point of points) {
            const convertedTime = convertEstTimestampToTradingView(point.timestamp);

            if (!seenObvTimes.has(convertedTime)) {
                seenObvTimes.add(convertedTime);

                obvData.push({
                    time: convertedTime,
                    value: point.value
                });
            }

            if (point.signal === null || seenSignalTimes.has(convertedTime)) {
                continue;
            }

            seenSignalTimes.add(convertedTime);

            signalData.push({
                time: convertedTime,
                value: point.signal
            });
        }

        obvSeries.setData(obvData);
        signalSeries.setData(signalData);

        createSeriesMarkers(
            obvSeries,
            buildSeriesMarkers({
                eventMarkers,
                focusedDateTime,
                points
            })
        );

        chart.timeScale().fitContent();

        const resizeObserver = new ResizeObserver(() => {
            if (!chartContainerRef.current) {
                return;
            }

            chart.applyOptions({
                width: chartContainerRef.current.clientWidth
            });
        });

        resizeObserver.observe(chartContainerRef.current);

        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
        };
    }, [eventMarkers, focusedDateTime, height, points]);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                    OBV
                </span>
                <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-400" />
                    Signal
                </span>
                <span className="text-zinc-500">Focused audit panel for OBV trend, cross, and divergence confirmation.</span>
            </div>
            <div className="w-full overflow-hidden rounded-lg border border-zinc-800 bg-[#111111]">
                <div ref={chartContainerRef} style={{ height: `${height}px`, width: '100%' }} />
            </div>
        </div>
    );
};

export default OBVLineAuditChart;
